import { useMemo } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import { IVasInfo } from '@r1-frontend/api-domru/api-profile/v1/vas/dto/ResponseDto';

import { wideBreakpoints } from '@r1-frontend/ui-react/components/layouts/wideContainer';
import { useMatchMedia } from '@r1-frontend/shared/hooks/useMatchMedia';

import { getMobileFirstMedia } from '~/src/features/domru-tv/packages/helpers/mobileFirstMedia';
import { selectFormattedSubscriptions } from '~/src/features/domru-tv/packages/store/selectors';
import { TDetailsType } from '~/src/features/domru-tv/packages/types';
import { TState } from '~/src/store';
import { ITvPackage } from '~/src/store/reducers/tvPackages';
import { selectFormattedTvPackages } from '~/src/store/selectors/tvPackages';
import { IFullMultibundle, IMultibundleAdditional, ISubscriptionAdditional, selectMultibundleAndSubscriptions } from '~/src/store/selectors/vas';

enum EVisibleWidgetsQuantity {
    SHORT = 3,
    DEFAULT = 4,
}

export type TSubscriptions = Partial<IVasInfo<IMultibundleAdditional>> & IVasInfo<ISubscriptionAdditional> & IFullMultibundle;

type TWidgets = ITvPackage | TSubscriptions;

export const useSectionData = <T extends TWidgets>(widgetType: TDetailsType, isShowAll: boolean): {
    isShowSectionButton: boolean,
    sectionWidgets: Array<T>,
} => {
    const mobileFirstLaptopBp = getMobileFirstMedia('laptop');
    const IsShortWidgetsList = useMatchMedia(`(min-width: ${mobileFirstLaptopBp}) and (max-width: ${wideBreakpoints.sDesktop})`);

    const maxVisibleWidgets = useMemo(
        () => IsShortWidgetsList ? EVisibleWidgetsQuantity.SHORT : EVisibleWidgetsQuantity.DEFAULT,
        [IsShortWidgetsList],
    );

    const {
        isAuth,
        pureTvPackages: { canConnect, connectedOutTariff, connectedInTariff },
        tvPackages,
        vasSubscriptions: { multibundle, subscriptions },
        shopItems,
    } = useSelector((state: TState) => ({
        isAuth: state.auth.isAuth,
        pureTvPackages: state.tvPackages,
        tvPackages: selectFormattedTvPackages(state),
        vasSubscriptions: selectMultibundleAndSubscriptions(state),
        shopItems: selectFormattedSubscriptions(state),
    }), shallowEqual);

    const sectionWidgets = (() => {
        switch (widgetType) {
            case 'packs':
                return isAuth ? [...canConnect, ...connectedOutTariff, ...connectedInTariff] : tvPackages;
            case 'subscriptions':
                return isAuth
                    ? multibundle
                        ? [multibundle, ...subscriptions]
                        : subscriptions
                    : shopItems;
            default:
                return [];
        }
    })();

    const isGreaterThanVisibility = sectionWidgets?.length > maxVisibleWidgets;

    if (!isShowAll) {
        return {
            isShowSectionButton: isGreaterThanVisibility,
            sectionWidgets: isGreaterThanVisibility ? sectionWidgets.slice(0, maxVisibleWidgets) : sectionWidgets,
        };
    }

    return {
        isShowSectionButton: false,
        sectionWidgets,
    };
};
